import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import NcImage from "shared/NcImage/NcImage";
import Prices from "./Prices";
import { CategoryData, Product, PRODUCTS } from "data/data";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useCartDataProvider } from "context/CartProductContext";
import { hasModifiers } from "utils/modifierHelpers";
import Cookies from "js-cookie";

export interface ProductCardProps {
  className?: string;
  data?: Product;
  categoryData?: CategoryData[];
  onCustomize?: (product: Product) => void;
}

const ProductCard: FC<ProductCardProps> = ({
  className = "",
  data = PRODUCTS[0],
  categoryData,
  onCustomize,
}) => {
  const { name, onlinePrice, description, imageUrl, isAvailable } = data;
  const { dispatch, openCartDrawer } = useCartDataProvider();
  const navigate = useNavigate();
  const merchantname = Cookies.get("merchantname");

  const isSimpleItem = !hasModifiers(data);

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch({ type: "QUICK_ADD", payload: data });

    toast.custom(
      (t) => (
        <Transition
          appear
          show={t.visible}
          className="p-3 max-w-sm w-full bg-white dark:bg-slate-800 shadow-lg rounded-xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 text-slate-900 dark:text-slate-100"
          enter="transition-all duration-200"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100 dark:bg-slate-700">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Added to cart
              </p>
            </div>
          </div>
        </Transition>
      ),
      {
        position: "bottom-center",
        id: `add-${data.id}`,
        duration: 1500,
      }
    );
  };

  const handleCardClick = () => {
    if (isSimpleItem) {
      handleQuickAdd({ stopPropagation: () => {} } as React.MouseEvent);
    } else if (onCustomize) {
      onCustomize(data);
    } else {
      navigate(`/${merchantname}/product-detail/${data.id}`);
    }
  };

  if (isAvailable !== 2 && isAvailable !== 3) return null;

  return (
    <div
      className={`nc-ProductCard group relative flex flex-col bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-700/10 overflow-hidden cursor-pointer hover:shadow-md dark:hover:shadow-slate-700/20 transition-shadow ${className}`}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${name}, $${onlinePrice?.toFixed(2)}`}
    >
      {/* Image -- fixed aspect ratio container */}
      <div className="relative w-full bg-stone-100 dark:bg-slate-700 overflow-hidden" style={{ paddingBottom: "100%" }}>
        <div className="absolute inset-0">
          <NcImage
            containerClassName="w-full h-full"
            src={imageUrl || ""}
            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        {/* Quick-add button for simple items */}
        {isSimpleItem && (
          <button
            onClick={handleQuickAdd}
            className="absolute bottom-2 right-2 w-10 h-10 bg-primary-500 dark:bg-primary-400 text-white rounded-full flex items-center justify-center shadow-lg z-10 active:scale-90 transition-transform hover:bg-primary-600 dark:hover:bg-primary-500"
            aria-label={`Add ${name} to cart`}
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        )}

        {/* Customize indicator for complex items */}
        {!isSimpleItem && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onCustomize) onCustomize(data);
              else navigate(`/${merchantname}/product-detail/${data.id}`);
            }}
            className="absolute bottom-2 right-2 h-10 px-3 bg-primary-500 dark:bg-primary-400 text-white rounded-full flex items-center justify-center gap-1.5 shadow-lg z-10 active:scale-95 transition-transform hover:bg-primary-600 dark:hover:bg-primary-500"
            aria-label={`Customize ${name}`}
          >
            <PlusIcon className="w-4 h-4" />
            <span className="text-xs font-semibold">Add</span>
          </button>
        )}
      </div>

      {/* Content -- min-h ensures uniform card height even without description */}
      <div className="p-3 flex flex-col gap-1 min-h-[5.5rem]">
        <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
          {name}
        </h3>
        <div className="flex items-center justify-between">
          <Prices
            onlinePrice={onlinePrice}
            contentClass="text-sm font-bold text-primary-600 dark:text-primary-400"
          />
          {!isSimpleItem && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
              Customizable
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
          {description || "\u00A0"}
        </p>
      </div>
    </div>
  );
};

export default ProductCard;
